import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { formatMoney } from "../../../utility/cashoutCalculation";
import { formatOdds } from "../../../utility/odds";
import {
  getBetCashoutQuote,
  executeBetCashout,
} from "../../actions/gameSettings";
import { toastAlert } from "../../lib/toastAlert";
import "./cashOutModal.css";

export default function CashOutModal({
  show,
  onClose,
  bet,
  email,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !bet?.ticketId || !email) {
      setQuote(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const loadQuote = async () => {
      setLoading(true);
      setError(null);
      setQuote(null);
      try {
        const res = await getBetCashoutQuote({
          ticketId: bet.ticketId,
          email,
          betType: bet.betType,
        });
        if (cancelled) return;
        if (res.status && res.result) {
          setQuote(res.result);
        } else {
          setError(res.message || "Cash out is not available for this bet.");
        }
      } catch {
        if (!cancelled) {
          setError("Unable to load cash out offer. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadQuote();
    return () => {
      cancelled = true;
    };
  }, [show, bet?.ticketId, bet?.betType, email]);

  const handleConfirm = async () => {
    if (!quote?.cashoutOffer || submitting) return;
    setSubmitting(true);
    try {
      const res = await executeBetCashout({
        ticketId: bet.ticketId,
        email,
        betType: bet.betType,
      });
      if (res.status) {
        toastAlert("success", res.message || "Cash out successful.");
        onSuccess?.();
        onClose();
      } else {
        toastAlert("error", res.message || "Cash out failed.");
      }
    } catch {
      toastAlert("error", "Cash out failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const netClass =
    quote?.cashoutNet > 0
      ? "cashout-net--positive"
      : quote?.cashoutNet < 0
      ? "cashout-net--negative"
      : "";

  return (
    <Modal show={show} onHide={onClose} centered className="cashout-modal">
      <Modal.Header closeButton>
        <Modal.Title className="fs16">Cash Out — Ticket #{bet?.ticketId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />{" "}
            <span className="ms-2">Calculating live offer…</span>
          </div>
        )}

        {!loading && error && (
          <div className="cashout-unavailable">
            <p className="mb-2 fw-semibold">Cash out unavailable</p>
            <p className="mb-0 text-muted fs13">{error}</p>
          </div>
        )}

        {!loading && quote && (
          <>
            <p
              className={`cashout-position cashout-position--${quote.position?.key || "unknown"}`}
            >
              {quote.position?.label}
            </p>

            <div className="cashout-breakdown">
              <div className="cashout-row">
                <span>Your stake (risk)</span>
                <strong>${formatMoney(quote.wager)}</strong>
              </div>
              <div className="cashout-row">
                <span>Original odds</span>
                <strong>{formatOdds(quote.originalOdds)}</strong>
              </div>
              <div className="cashout-row">
                <span>Current live odds</span>
                <strong>{formatOdds(quote.liveOdds)}</strong>
              </div>
              <div className="cashout-row">
                <span>Potential payout if held</span>
                <strong>${formatMoney(quote.payout)}</strong>
              </div>
              <div className="cashout-row cashout-row--highlight">
                <span>Cash out offer now</span>
                <strong className="cashout-offer-amount">
                  ${formatMoney(quote.cashoutOffer)}
                </strong>
              </div>
              <div className={`cashout-row ${netClass}`}>
                <span>Net vs stake</span>
                <strong>
                  {quote.cashoutNet >= 0 ? "+" : ""}$
                  {formatMoney(quote.cashoutNet)}
                </strong>
              </div>
            </div>

            <p className="cashout-disclaimer fs12 text-muted mt-3 mb-0">
              Offer is based on current live odds and includes a{" "}
              {quote.riskFactorPercent}% platform risk factor. Once confirmed,
              this bet is settled immediately and cannot be reversed.
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        {quote?.cashoutOffer ? (
          <Button
            variant="success"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting
              ? "Processing…"
              : `Confirm Cash Out — $${formatMoney(quote.cashoutOffer)}`}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
